// @flow

// Types
import type {
  FieldBorderOperation,
} from '../../types'

import type {
  FieldBorder,
} from 'fc-model-fieldborder/types'

// Libs
import _map from 'lodash/map'
import _every from 'lodash/every'
import _size from 'lodash/size'
import _forEach from 'lodash/forEach'
import _find from 'lodash/find'

// util
import getUrl from 'hfty-utils/url'

// Operator
import operator from '../operator'

import validateOperation from './validateOperation'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

// selectors
import token$ from 'fc-auth/src/selectors/token'

// actions
import { borderUploaded } from 'hfty-assets'

// constants
import { UPDATE_FIELD_BORDER_OPERATION } from 'fc-upload-borders'
import {
  PROP_SHAPE_DATA,
  PROP_TYPE,
  PROP_ASSET,
} from 'fc-model-fieldborder'

type OperationsArgs = {
  operations$: (state: Object) => Array<FieldBorderOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  yield take([operator.actionTypes.add()])
  yield fork(watchUpdateOperation)
  yield fork(handleOperations, {operations$})
}

export function *watchUpdateOperation (): Generator<*, void, *> {
  while (true) {
    const { payload } = yield take([UPDATE_FIELD_BORDER_OPERATION])
    yield call(handleUpdateOperation, payload)
  }
}

export function *handleUpdateOperation (operation: FieldBorderOperation): Generator<*, void, *> {
  const validatedOperation = yield call(validateOperation, operation)
  const action: Action<Object> = yield operator.actionCreators.updateOperation(validatedOperation)
  yield put(action)
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = true // Cancelled
    }
  }
}

export function *handleSubmitOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)

  const nextOperations: Array<FieldBorderOperation> =
  yield _map(operations, function (operation: FieldBorderOperation): Object {
      return call(handleSubmitOperation, operation)
    })

  return _every(nextOperations, function (operation: FieldBorderOperation): boolean {
    return operation.success
  })
}

export function *handleSubmitOperation (operation: FieldBorderOperation): Generator<*, CommentOperation, *> {
  const validatedOperation = yield call(validateOperation, operation)

  if (_size(validatedOperation.errors)) {
    const nextOperation = {
      ...validatedOperation,
      triedSubmission: true,
    }
    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)
    return nextOperation
  }

  const model: FieldBorder = operation.model

  let headers = {}
  const token = yield select(token$)
  const method = 'POST'
  if (token) {
    headers['Authorization'] = `Token ${token}`
  }

  const body = new FormData()

  _forEach(model[PROP_SHAPE_DATA], (file: Object) => { body.append('files', file) })
  body.append('asset', model[PROP_ASSET])
  body.append('type', model[PROP_TYPE])

  const shp = _find(model[PROP_SHAPE_DATA], ({name}: {name: string}): string => name && name.match(/shp$/))
  body.append('name', shp && shp.name)
  const options = {
    method,
    headers,
    body,
  }

  const nextOperation = {
    ...operation,
    submitting: true,
    triedSubmission: true,
    success: false,
  }
  const action = operator.actionCreators.updateOperation(nextOperation)
  yield put(action)

  const response = yield call(postBorder, options)
  if (response.error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      triedSubmission: true,
      success: false,
      errors: [{error: response.error.message}],
    }
    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const updateField = borderUploaded({assetId: model[PROP_ASSET], response})

    yield put(updateField)

    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.cancelOperation(nextOperation)
    yield put(action)

    return nextOperation
  }
}

function postBorder (options: Object): Object {
  return fetch(getUrl({url: 'spatial/'}), options)
    .then((response: Object): Object => {
      if (response.status === 201) {
        return Promise.resolve(response)
      } else {
        return Promise.reject(new Error(response.statusText))
      }
    })
    .then((response: Object): Object => response.json())
    .catch((error: Object): Object => {
      return {error}
    })
    .then((data: Object): Object => {
      return data
    })
}
